import { Activity, useEffect, useRef, useState } from 'react'
import type { Actor, AttachFileArgs, BoardAttachment } from '../api/types'
import { errorMessage } from '../api/client'
import { wireFiles, MAX_FILE_BYTES } from '../code/attachments'
import { bytesText } from '../transcript/SentFiles'
import { useBoardState, useStore } from '../store/context'
import { useUnsavedChanges } from '../components/unsaved'
import { ZoomOverlay } from '../markdown/ZoomOverlay'
import { useTabOwner } from '../workspace/context'
import './attachments.css'

type Target = Pick<AttachFileArgs, 'issue' | 'plan' | 'entry' | 'comment'>

function sameAuthor(a: Actor, b: Actor | undefined): boolean {
  return a.kind === 'person' && b?.kind === 'person' ? a.member === b.member
    : a.kind === 'agent' && b?.kind === 'agent' && a.session === b.session
}

/** Files and links on this issue, plan or comment. A comment's author alone adds to it. */
export function Attachments({ target, author, readOnly = false }: { target: Target; author?: Actor; readOnly?: boolean }) {
  const store = useStore()
  const { attachments, me } = useBoardState()
  const files = [...attachments.values()].filter((a) => a.issue === (target.issue ?? null) && a.plan === (target.plan ?? null)
    && a.entry === (target.entry ?? null) && a.comment === (target.comment ?? null))
  const writable = !readOnly && (!author || sameAuthor(author, me?.actor))
  const picker = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [linking, setLinking] = useState(false)
  const [url, setUrl] = useState('')
  const [title, setTitle] = useState('')
  useUnsavedChanges(linking && (url.trim() !== '' || title.trim() !== ''))

  const upload = async (found: File[]) => {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      for (const file of found) {
        if (file.size > MAX_FILE_BYTES || file.size === 0) throw new Error(`“${file.name}” must hold between 1 byte and 20 MB.`)
        const [wire] = await wireFiles([file])
        const result = await store.attachFile({ ...target, file: wire })
        if (!result.ok) throw new Error(result.message)
      }
    } catch (err) { setError(errorMessage(err)) }
    finally { setBusy(false) }
  }

  const addLink = async () => {
    if (busy || !url.trim()) return
    setBusy(true)
    setError(null)
    const result = await store.attachFile({ ...target, url: url.trim(), ...(title.trim() ? { title: title.trim() } : {}) })
    setBusy(false)
    if (result.ok) { setUrl(''); setTitle(''); setLinking(false) }
    else setError(result.message)
  }

  if (!writable && files.length === 0) return null

  return (
    <section className="board-attachments" aria-label="Attachments">
      {!(target.entry || target.comment) && <h2>Attachments</h2>}
      {files.length > 0 && <ul className="attachment-list">{files.map((file) => <AttachmentItem key={file.id} file={file} />)}</ul>}
      {writable && <div className="attachment-actions">
        <input ref={picker} type="file" multiple hidden aria-label="Choose attachments" disabled={busy} onChange={(e) => {
          const found = Array.from(e.target.files ?? [])
          e.target.value = ''
          void upload(found)
        }} />
        <button type="button" className="button button-quiet button-small" disabled={busy} onClick={() => picker.current?.click()}>{busy ? 'Attaching…' : 'Attach file'}</button>
        <button type="button" className="button button-quiet button-small" disabled={busy} onClick={() => setLinking(!linking)}>Add link</button>
      </div>}
      {linking && <div className="attachment-link-form">
        <input className="input" aria-label="Link URL" type="url" value={url} placeholder="https://…" maxLength={2000} onChange={(e) => setUrl(e.target.value)} disabled={busy} />
        <input className="input" aria-label="Link title" value={title} placeholder="Title (optional)" maxLength={200} onChange={(e) => setTitle(e.target.value)} disabled={busy} />
        <button type="button" className="button" disabled={busy || !url.trim()} onClick={() => void addLink()}>Attach link</button>
      </div>}
      {error && <p role="alert">{error}</p>}
    </section>
  )
}

function AttachmentItem({ file }: { file: BoardAttachment }) {
  const store = useStore()
  const [preview, setPreview] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [enlarged, setEnlarged] = useState(false)
  const owner = useTabOwner()
  const image = file.mime_type?.startsWith('image/') ?? false
  useEffect(() => {
    if (!image) return
    let current = true
    let objectUrl: string | null = null
    void store.attachmentFile(file.id).then((blob) => {
      if (!current) return
      objectUrl = URL.createObjectURL(blob)
      setPreview(objectUrl)
    }).catch((err) => { if (current) setError(errorMessage(err)) })
    return () => { current = false; if (objectUrl) URL.revokeObjectURL(objectUrl) }
  }, [store, file.id, image])

  const download = async () => {
    setBusy(true)
    setError(null)
    try {
      const blob = await store.attachmentFile(file.id)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = file.title
      link.click()
      // Give the browser one task to start reading before releasing it.
      setTimeout(() => URL.revokeObjectURL(url), 0)
    } catch (err) { setError(errorMessage(err)) }
    finally { setBusy(false) }
  }

  return <li>
    {file.url ? <a href={file.url} target="_blank" rel="noreferrer noopener">{file.title}</a> : <>
      {preview && <button type="button" className="attachment-enlarge" aria-label={`Enlarge ${file.title}`} onClick={() => setEnlarged(true)}><img src={preview} alt={file.title} className="attachment-preview" loading="lazy" /></button>}
      {preview && <Activity mode={owner ? 'visible' : 'hidden'}>{enlarged && <ZoomOverlay target={{ kind: 'image', src: preview, alt: file.title }} onClose={() => setEnlarged(false)} />}</Activity>}
      <button type="button" className="button button-quiet" onClick={() => void download()} disabled={busy}>{file.title} <span className="muted">{bytesText(file.size)}</span></button>
    </>}
    {error && <p role="alert">{error}</p>}
  </li>
}
